import React, { useEffect, useMemo, useState } from "react";
import useApiStable from "../../utils/useApiStable";

const ROUND_LABELS = {
  ROUND_16: "Achtelfinale",
  QUARTER_FINAL: "Viertelfinale",
  SEMI_FINAL: "Halbfinale",
  FINAL: "Finale",
};

const slotKey = (matchNumber, slot) => `${matchNumber}:${slot}`;
const optionValue = (groupId, rank) => `${groupId}:${rank}`;

/**
 * Admin-UI zur freien Konfiguration, welcher Gruppenplatz (Gruppe + Rang) in welches Match der
 * ersten K.O.-Runde einzieht, sowie zur individuellen Qualifikanten-Anzahl je Gruppe. Wird auf der
 * Turnierverwaltung angezeigt, solange der K.O.-Baum noch nicht angelegt ist (unabhängig vom
 * Turnierstart). Persistiert über GET/PUT /tournaments/knockout/{tournamentId}/seed-config sowie
 * PATCH /tournaments/groups/{groupId}/qualifiers.
 */
const KnockoutSeedConfig = ({ tournamentId, numberOfGroups, onBracketSizeChange, onDirectFinalFormatEligibleChange }) => {
  const api = useApiStable();
  const [config, setConfig] = useState(null);
  const [slotsByKey, setSlotsByKey] = useState({});
  const [qualifiersByGroupId, setQualifiersByGroupId] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  const applyConfig = (data) => {
    setConfig(data);
    const slotMap = {};
    (data.slots || []).forEach((slot) => {
      slotMap[slotKey(slot.matchNumber, slot.slot)] = optionValue(slot.groupId, slot.groupRank);
    });
    setSlotsByKey(slotMap);
    const qualifiersMap = {};
    (data.availableGroups || []).forEach((group) => {
      qualifiersMap[group.groupId] = group.qualifiers;
    });
    setQualifiersByGroupId(qualifiersMap);
    if (typeof onBracketSizeChange === "function") {
      onBracketSizeChange(data.bracketSize || 0);
    }
    // Genau 2 Gruppen mit je genau 1 Qualifikant ("Direktfinale-Format"): dort ist ein Spiel um
    // Platz 3 auch ohne Halbfinale moeglich (Gruppenzweite spielen direkt gegeneinander, obwohl sie
    // keine "Qualifikanten" sind) - siehe KnockoutSeedService#isDirectFinalFormat.
    if (typeof onDirectFinalFormatEligibleChange === "function") {
      onDirectFinalFormatEligibleChange(Boolean(data.directFinalFormatEligible));
    }
  };

  const loadConfig = () => {
    if (!tournamentId) return;
    setLoading(true);
    setError("");
    api
      .get(`/tournaments/knockout/${tournamentId}/seed-config`)
      .then(applyConfig)
      .catch((err) => setError("Seeding konnte nicht geladen werden: " + err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadConfig();
  }, [tournamentId, numberOfGroups]); // eslint-disable-line

  const matches = useMemo(() => {
    if (!config || !config.bracketSize) return [];
    return Array.from({ length: config.bracketSize / 2 }, (_, i) => i + 1);
  }, [config]);

  const groupOptions = useMemo(() => {
    if (!config) return [];
    const options = [];
    for (const group of config.availableGroups || []) {
      for (let rank = 1; rank <= group.qualifiers; rank++) {
        options.push({ value: optionValue(group.groupId, rank), label: `${group.name} - Platz ${rank}` });
      }
    }
    return options;
  }, [config]);

  const usedValues = useMemo(() => new Set(Object.values(slotsByKey)), [slotsByKey]);

  const handleGroupQualifiersChange = (groupId, value) => {
    setQualifiersByGroupId((prev) => ({ ...prev, [groupId]: value }));
  };

  const handleGroupQualifiersBlur = async (groupId) => {
    const currentGroup = (config?.availableGroups || []).find((g) => g.groupId === groupId);
    const value = Number(qualifiersByGroupId[groupId]);
    if (!currentGroup || !value || value < 1 || value === currentGroup.qualifiers) {
      if (currentGroup) {
        setQualifiersByGroupId((prev) => ({ ...prev, [groupId]: currentGroup.qualifiers }));
      }
      return;
    }
    setError("");
    try {
      await api.patch(`/tournaments/groups/${groupId}/qualifiers`, { qualifiers: value });
      loadConfig();
    } catch (err) {
      setError("Qualifikanten konnten nicht gespeichert werden: " + err.message);
      setQualifiersByGroupId((prev) => ({ ...prev, [groupId]: currentGroup.qualifiers }));
    }
  };

  const handleSlotChange = (matchNumber, slot, value) => {
    setSlotsByKey((prev) => ({ ...prev, [slotKey(matchNumber, slot)]: value }));
  };

  // Setzt die Matrix (nur lokal, noch ungespeichert) auf den automatisch berechneten
  // Standard-Vorschlag zurück. Erst "Seeding speichern" persistiert das Ergebnis.
  const handleGenerateDefault = async () => {
    if (!tournamentId) return;
    setLoading(true);
    setError("");
    setSaveMessage("");
    try {
      const defaultConfig = await api.get(`/tournaments/knockout/${tournamentId}/seed-config/default`);
      setConfig(defaultConfig);
      const map = {};
      (defaultConfig.slots || []).forEach((slot) => {
        map[slotKey(slot.matchNumber, slot.slot)] = optionValue(slot.groupId, slot.groupRank);
      });
      setSlotsByKey(map);
      setSaveMessage("Standard-Seeding vorgeschlagen - bitte prüfen und speichern.");
    } catch (err) {
      setError("Standard-Seeding konnte nicht erzeugt werden: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;
    const slots = matches.flatMap((matchNumber) =>
      ["TEAM1", "TEAM2"].map((slot) => {
        const raw = slotsByKey[slotKey(matchNumber, slot)];
        if (!raw) return null;
        const [groupId, groupRank] = raw.split(":").map(Number);
        return { matchNumber, slot, groupId, groupRank };
      })
    );

    if (slots.some((s) => s === null)) {
      setError("Bitte für jedes Match beide Plätze auswählen, bevor gespeichert wird.");
      return;
    }

    setLoading(true);
    setError("");
    setSaveMessage("");
    try {
      const updated = await api.put(`/tournaments/knockout/${tournamentId}/seed-config`, { slots });
      applyConfig(updated);
      setSaveMessage("Seeding gespeichert.");
    } catch (err) {
      setError("Seeding konnte nicht gespeichert werden: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!tournamentId) return null;

  return (
    <div className="adminpanel-seedconfig">
      <div className="adminpanel-field">
        <span>Qualifikanten pro Gruppe</span>
        {(config?.availableGroups || []).map((group) => (
          <label
            key={group.groupId}
            className="adminpanel-field"
            style={{ flexDirection: "row", alignItems: "center", gap: 8, marginTop: 4 }}
          >
            <span style={{ minWidth: 140 }}>{group.name}</span>
            <input
              type="number"
              min={1}
              value={qualifiersByGroupId[group.groupId] ?? group.qualifiers}
              onChange={(e) => handleGroupQualifiersChange(group.groupId, e.target.value)}
              onBlur={() => handleGroupQualifiersBlur(group.groupId)}
              className="groupphase-input"
              style={{ maxWidth: 80 }}
            />
          </label>
        ))}
      </div>

      {config && (
        <p className="adminpanel-hint">
          {config.numberOfGroups} Gruppen, Summe Qualifikanten = {config.bracketSize} Teams
          {config.firstRound ? (
            <> → Start-Runde: <strong>{ROUND_LABELS[config.firstRound] || config.firstRound}</strong></>
          ) : (
            " — ungültig, Summe muss 2, 4, 8 oder 16 ergeben"
          )}
          {config.directFinalFormatEligible && (
            <>
              {" "}
              (Direktfinale-Format: die Gruppenersten spielen direkt das Finale, die Gruppenzweiten
              direkt das Spiel um Platz 3 - kein Halbfinale)
            </>
          )}
        </p>
      )}

      {error && <p className="adminpanel-warning-text">{error}</p>}
      {saveMessage && !error && <p className="adminpanel-status-text">{saveMessage}</p>}

      {config && matches.length > 0 && (
        <div className="adminpanel-seedconfig-matrix">
          <table className="adminpanel-seedconfig-table">
            <thead>
              <tr>
                <th>Match</th>
                <th>Platz 1</th>
                <th>Platz 2</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((matchNumber) => (
                <tr key={matchNumber}>
                  <td>Match {matchNumber}</td>
                  {["TEAM1", "TEAM2"].map((slot) => {
                    const currentValue = slotsByKey[slotKey(matchNumber, slot)] || "";
                    return (
                      <td key={slot}>
                        <select
                          value={currentValue}
                          onChange={(e) => handleSlotChange(matchNumber, slot, e.target.value)}
                          className="groupphase-select"
                        >
                          <option value="">-- auswählen --</option>
                          {groupOptions.map((option) => (
                            <option
                              key={option.value}
                              value={option.value}
                              disabled={usedValues.has(option.value) && option.value !== currentValue}
                            >
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          <div className="adminpanel-inline-row" style={{ marginTop: 12 }}>
            <button className="groupphase-button" onClick={handleGenerateDefault} disabled={loading}>
              Standard-Seeding vorschlagen
            </button>
            <button className="groupphase-button primary" onClick={handleSave} disabled={loading}>
              Seeding speichern
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnockoutSeedConfig;
